/**
 * Centralized Portal Configuration
 * 
 * This file contains the centralized configuration for all portals, including:
 * - Portal definitions (id, name, path, icon)
 * - Role-based access control for each portal
 * - Portal metadata
 * 
 * IMPORTANT: This is the single source of truth for portal configurations.
 * All portal switcher components and protected routes should use this configuration.
 */

import Person from '@mui/icons-material/Person'
import Home from '@mui/icons-material/Home'
import Business from '@mui/icons-material/Business'
import LocalHospital from '@mui/icons-material/LocalHospital'
import MonitorHeart from '@mui/icons-material/MonitorHeart'
import TvIcon from '@mui/icons-material/Tv'
import Support from '@mui/icons-material/Support'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { PORTAL_IDS } from './portalIds'
import { isPortalEnabledByAvailability } from './portalAvailabilityRules'

export { PORTAL_CHOOSER_ID, PORTAL_IDS } from './portalIds'

export const SUPPORT_PORTAL_ROLES = [
  'ROLE_ADMIN',
  'ROLE_SUB_ADMIN',
  'ROLE_APP_MANAGER',
  'ROLE_DEVELOPER',
  'ROLE_MANAGEMENT',
  'ROLE_THERAPY_SUP_L1',
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_CLINIC_ROSTER_MANAGER',
  'ROLE_CLINIC_ROSTER_ADMIN',
  'ROLE_VC_AUDIT_ADMIN',
  'ROLE_VC_AUDIT_MANAGER',
  'ROLE_DOCSIDE_ADMIN',
  'ROLE_DOCSIDE_MANAGER',
  'ROLE_SUPPORT',
  'ROLE_SUPPORT_USER',
  'ROLE_PAYMENT_SUPPORT',
  'ROLE_READ_ONLY_RESTRICTED',
]

export const MENTOR_PORTAL_ROLES = [
  'ROLE_DOCTOR',
  'ROLE_TEACHER',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
  'ROLE_CREATOR',
]

export const MENTOR_DEFAULT_HANDOFF_ROLES = [
  'ROLE_DOCTOR',
  'ROLE_TEACHER',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
]

export const MENTOR_CLINIC_ACCESS_ROLES = [
  'ROLE_DOCTOR',
  'ROLE_THERAPIST',
  'ROLE_SCREENER',
]

export const CLINIC_PORTAL_ROLES = [
  'ROLE_CLINIC_OWNER',
  'ROLE_CLINIC_MANAGER',
  'ROLE_CLINIC_ADMIN',
  'ROLE_CLINIC_PROSPECT',
  'ROLE_PHARMACIST',
]

export const CLINIC_ASSISTANT_PORTAL_ROLES = ['ROLE_CLINIC_ASSISTANT']
export const KIOSK_PORTAL_ROLES = ['ROLE_KIOSK', 'ROLE_AUMHUM_SCREEN', 'ROLE_CHAMBER_SCREEN']
export const DISPLAY_PORTAL_ROLES = ['ROLE_OOH']
export const BUSINESS_PORTAL_ROLES = ['ROLE_BUSINESS_CUSTOMER', 'ROLE_BUSINESS_PROSPECT']
export const PATIENT_PORTAL_ROLES = ['ROLE_PATIENT']
export const PATIENT_PROSPECT_PORTAL_ROLES = ['ROLE_PATIENT_PROSPECT']
export const WELLNESS_PORTAL_ROLES = ['ROLE_USER', 'ROLE_PROFESSIONAL_USER']
export const CONTROL_CENTER_PORTAL_ROLES = [
  'ROLE_ADMIN',
  'ROLE_SUB_ADMIN',
  'ROLE_APP_MANAGER',
  'ROLE_DEVELOPER',
  'ROLE_CLINIC_OWNER',
]

function hasAnyRole(userRoles, candidateRoles) {
  return Array.isArray(userRoles) && candidateRoles.some((role) => userRoles.includes(role))
}

function resolveMentorDefaultPath(userRoles) {
  return hasAnyRole(userRoles, MENTOR_CLINIC_ACCESS_ROLES)
    ? '/mentor-home/my-clinic-associations'
    : '/mentor-home/preferences-and-settings'
}

/**
 * Portal implementation registry.
 * Each implemented portal defines:
 * - id: Stable identifier used by frontend routing and backend user overrides
 * - name: Display name shown in portal switchers
 * - path: Route path for the portal switcher
 * - defaultPath: Optional login-handoff landing path, static or role-aware
 * - icon: Material-UI icon component
 * - roles: Role strings that grant access to this portal implementation
 * - defaultHandoffRoles: Optional narrower role list used for direct login routing
 * - flowKey: Value stored in lastChosenFlow for user preference routing
 * - external: Whether the portal is an external link
 */
export const PORTAL_REGISTRY = Object.freeze({
  [PORTAL_IDS.SUPPORT]: {
    id: PORTAL_IDS.SUPPORT,
    name: 'Support Portal',
    path: '/support-home',
    icon: Support,
    roles: SUPPORT_PORTAL_ROLES,
    flowKey: PORTAL_IDS.SUPPORT,
    external: false,
  },
  [PORTAL_IDS.MENTOR]: {
    id: PORTAL_IDS.MENTOR,
    name: 'Clinic Practitioner App',
    path: '/mentor-home',
    defaultPath: resolveMentorDefaultPath,
    icon: Person,
    roles: MENTOR_PORTAL_ROLES,
    defaultHandoffRoles: MENTOR_DEFAULT_HANDOFF_ROLES,
    preferenceRoles: MENTOR_DEFAULT_HANDOFF_ROLES,
    flowKey: PORTAL_IDS.MENTOR,
    external: false,
  },
  [PORTAL_IDS.CLINIC]: {
    id: PORTAL_IDS.CLINIC,
    name: 'Clinic Owners App',
    path: '/clinic-home',
    icon: LocalHospital,
    roles: CLINIC_PORTAL_ROLES,
    flowKey: PORTAL_IDS.CLINIC,
    external: false,
  },
  [PORTAL_IDS.CLINIC_ASSISTANT]: {
    id: PORTAL_IDS.CLINIC_ASSISTANT,
    name: 'Clinic Assistant Portal',
    path: '/ca-home',
    icon: LocalHospital,
    roles: CLINIC_ASSISTANT_PORTAL_ROLES,
    flowKey: PORTAL_IDS.CLINIC_ASSISTANT,
    external: false,
  },
  [PORTAL_IDS.KIOSK]: {
    id: PORTAL_IDS.KIOSK,
    name: 'KIOSK (AUMHUM Screen)',
    path: '/kiosk',
    icon: TvIcon,
    roles: KIOSK_PORTAL_ROLES,
    flowKey: PORTAL_IDS.KIOSK,
    external: false,
  },
  [PORTAL_IDS.DISPLAY]: {
    id: PORTAL_IDS.DISPLAY,
    name: 'Display (OOH / Token)',
    path: '/display',
    icon: TvIcon,
    roles: DISPLAY_PORTAL_ROLES,
    flowKey: PORTAL_IDS.DISPLAY,
    external: false,
  },
  [PORTAL_IDS.BUSINESS]: {
    id: PORTAL_IDS.BUSINESS,
    name: 'Business Portal',
    path: '/workplace',
    icon: Business,
    roles: BUSINESS_PORTAL_ROLES,
    flowKey: PORTAL_IDS.BUSINESS,
    external: false,
  },
  [PORTAL_IDS.PATIENT]: {
    // The patient portal at /patient-home is the dedicated care-management
    // surface for ROLE_PATIENT users. Distinct from the Wellness App
    // (consumer meditation / journaling / habits) which is gated on
    // ROLE_USER only — ROLE_PATIENT no longer grants access to /. A user
    // who needs both apps must hold both roles.
    id: PORTAL_IDS.PATIENT,
    name: 'Patient App',
    path: '/patient-home',
    icon: MonitorHeart,
    roles: PATIENT_PORTAL_ROLES,
    flowKey: PORTAL_IDS.PATIENT,
    external: false,
  },
  [PORTAL_IDS.PATIENT_PROSPECT]: {
    id: PORTAL_IDS.PATIENT_PROSPECT,
    name: 'Care Request',
    path: '/care/status',
    icon: MonitorHeart,
    roles: PATIENT_PROSPECT_PORTAL_ROLES,
    flowKey: PORTAL_IDS.PATIENT_PROSPECT,
    external: false,
  },
  [PORTAL_IDS.CONSUMER]: {
    id: PORTAL_IDS.CONSUMER,
    name: 'Wellness App',
    path: '/',
    defaultPath: '/wellness',
    icon: Home,
    roles: WELLNESS_PORTAL_ROLES,
    flowKey: PORTAL_IDS.CONSUMER,
    external: false,
  },
  [PORTAL_IDS.CONTROL_CENTER]: {
    id: PORTAL_IDS.CONTROL_CENTER,
    name: 'Control Center',
    path: APP_CONSTANTS.CONTROL_CENTER_LINK,
    icon: AdminPanelSettings,
    roles: CONTROL_CENTER_PORTAL_ROLES,
    external: true,
  },
})

export const PORTAL_CONFIG = Object.freeze(Object.values(PORTAL_REGISTRY))

/**
 * Get all roles that have access to a specific portal
 * @param {string} portalId - The portal ID
 * @returns {string[]} Array of role strings
 */
export function getRolesForPortal(portalId) {
  const portal = PORTAL_CONFIG.find((p) => p.id === portalId)
  return portal ? portal.roles : []
}

/**
 * Get portal configuration by ID
 * @param {string} portalId - The portal ID
 * @returns {Object|null} Portal configuration object or null
 */
export function getPortalById(portalId) {
  return PORTAL_REGISTRY[portalId] || null
}

/**
 * Get portal configuration by path
 * @param {string} path - The portal path
 * @returns {Object|null} Portal configuration object or null
 */
export function getPortalByPath(path) {
  return PORTAL_CONFIG.find((p) => p.path === path) || null
}

export function getPortalFlowKey(portalOrId) {
  const portal = typeof portalOrId === 'string' ? getPortalById(portalOrId) : portalOrId
  return portal?.flowKey || null
}

export function getPortalDefaultPath(portalOrId, userRoles = []) {
  const portal = typeof portalOrId === 'string' ? getPortalById(portalOrId) : portalOrId
  if (!portal) return ''

  if (typeof portal.defaultPath === 'function') {
    return portal.defaultPath(userRoles)
  }

  return portal.defaultPath || portal.path || ''
}

export function getPortalDefaultHandoffRoles(portalOrId) {
  const portal = typeof portalOrId === 'string' ? getPortalById(portalOrId) : portalOrId
  return portal?.defaultHandoffRoles || portal?.roles || []
}

export function getPortalPreferenceRoles(portalOrId) {
  const portal = typeof portalOrId === 'string' ? getPortalById(portalOrId) : portalOrId
  return portal?.preferenceRoles || getPortalDefaultHandoffRoles(portal)
}

/**
 * Check if a user has access to a portal based on their roles
 * @param {string[]} userRoles - Array of user role strings
 * @param {string} portalId - The portal ID
 * @returns {boolean} True if user has access, false otherwise
 */
export function hasPortalAccess(userRoles, portalId, portalAvailabilityOverrides) {
  const portal = getPortalById(portalId)
  if (!portal) return false

  return (
    portal.roles.some((role) => userRoles?.includes(role)) &&
    isPortalEnabledByAvailability(portal, userRoles, portalAvailabilityOverrides)
  )
}

/**
 * Get all portals a user has access to
 * @param {string[]} userRoles - Array of user role strings
 * @returns {Object[]} Array of portal configuration objects
 */
export function getAvailablePortals(userRoles, portalAvailabilityOverrides) {
  return PORTAL_CONFIG.filter((portal) =>
    hasPortalAccess(userRoles, portal.id, portalAvailabilityOverrides))
}
